import { useQueryParams } from "./use-query-params";

type FilterValues<K extends string> = Record<K, string | undefined>;
type FilterChanges<K extends string> = Partial<Record<K, string | number | undefined>>;

interface IUseUrlListState<K extends string> {
  /** Current page (1-based; 1 when absent). */
  page: number;
  /** Current value of each declared filter (undefined when absent). */
  filters: FilterValues<K>;
  setPage: (page: number) => void;
  /** Update one or more filters; always resets back to page 1. */
  setFilters: (changes: FilterChanges<K>) => void;
}

/**
 * URL-as-source-of-truth list state (page + a declared set of filters), the search-params
 * counterpart of `use-filter-state`. Centralises the conventions: page 1 stays out of the URL and
 * any filter change resets to page 1. Shared by the products overview and the sync dashboard.
 */
export const useUrlListState = <K extends string>(filterKeys: readonly K[]): IUseUrlListState<K> => {
  const { get, set } = useQueryParams();

  const page = Number(get("page")) || 1;

  const filters = filterKeys.reduce((acc, key) => {
    acc[key] = get(key);
    return acc;
  }, {} as FilterValues<K>);

  const setPage = (nextPage: number) => set({ page: nextPage > 1 ? nextPage : undefined });
  const setFilters = (changes: FilterChanges<K>) => set({ ...changes, page: undefined });

  return { page, filters, setPage, setFilters };
};
